import { HttpError } from '@/lib/httpError'

interface User {
  id: string
  name: string
  account: string
  type: string
  platform_role: string
  contract_agreement: boolean | null
  totp_enabled: boolean
  last_login_at?: string
  created_at: string
  updated_at: string
}

export interface ProjectRequest {
  id: string
  requestor_id: string
  type: string
  target_id: string
  payload: string
  status: 'pending' | 'approved' | 'rejected'
  created_at: string
  processed_at?: string
  reason?: string
  requestor: User
  approver?: User
}

interface ProjectRequestsResponse {
  limit: number
  offset: number
  requests: ProjectRequest[]
}

export interface RequestPayloadUpdateProjectRole {
  user_id: string
  role: 'owner' | 'admin' | 'member' | 'viewer' | 'observer'
}

export interface RequestPayloadAddProjectMember {
  user_id: string
  role: 'owner' | 'admin' | 'member' | 'viewer' | 'observer'
}

export interface RequestPayloadRemoveProjectMember {
  user_id: string
}

class ProjectRequestService {
  private baseUrl = '/api/v2/requests/project'

  async getPendingRequests(projectId: string, offset: number = 0, limit: number = 100): Promise<ProjectRequestsResponse> {
    const response = await fetch(`${this.baseUrl}/${projectId}/pending?offset=${offset}&limit=${limit}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new HttpError(errorText || 'Failed to fetch pending requests', response.status)
    }

    return response.json()
  }

  async getApprovedRequests(projectId: string, offset: number = 0, limit: number = 100): Promise<ProjectRequestsResponse> {
    const response = await fetch(`${this.baseUrl}/${projectId}/approved?offset=${offset}&limit=${limit}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new HttpError(errorText || 'Failed to fetch approved requests', response.status)
    }

    return response.json()
  }

  async getRejectedRequests(projectId: string, offset: number = 0, limit: number = 100): Promise<ProjectRequestsResponse> {
    const response = await fetch(`${this.baseUrl}/${projectId}/rejected?offset=${offset}&limit=${limit}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new HttpError(errorText || 'Failed to fetch rejected requests', response.status)
    }

    return response.json()
  }

  async approveRequest(requestId: string, reason?: string): Promise<void> {
    const response = await fetch(`${this.baseUrl}/${requestId}/approve`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ reason: reason || '' }),
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new HttpError(errorText || 'Failed to approve request', response.status)
    }
  }

  async rejectRequest(requestId: string, reason: string): Promise<void> {
    const response = await fetch(`${this.baseUrl}/${requestId}/reject`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ reason }),
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new HttpError(errorText || 'Failed to reject request', response.status)
    }
  }
}

export const projectRequestService = new ProjectRequestService()
